import { useEffect } from "react";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { trackPageView } from "@/hooks/useAnalytics";
import { getJobs, getTagStats } from "@/lib/api";
import type { PaginatedJobs } from "@/types/db";
import SearchClient from "@/components/search/searchClient";
import Hero from "@/components/hero";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import { buildFaqSchema, buildMinimalFaqSchema } from "@/lib/faqSchema";
import type { TagStatsData } from "@/lib/faqSchema";
import { jsonLdSafe } from "@/lib/jsonLd";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");

export const Route = createFileRoute("/remote/")({
  loader: async () => {
    const [initialData, tagStats] = await Promise.all([
      getJobs({
        data: { limit: 20, tags: ["remote-data-jobs"] },
      }) as Promise<PaginatedJobs>,
      getTagStats({ data: "remote-data-jobs" }).catch(() => null),
    ]);

    return {
      initialJobs: initialData.jobs,
      initialTotal: initialData.total,
      tagStats: tagStats as TagStatsData | null,
    };
  },
  head: ({ loaderData }) => {
    const title = "Remote Data Jobs | Data First Jobs";
    const count = loaderData?.initialTotal ?? 0;
    const description = `Browse ${count}+ remote data jobs. Find remote data engineering, analytics, and data science roles on Data First Jobs.`;
    const url = `${SITE_URL}/remote`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: RemoteIndexPage,
});

function RemoteIndexPage() {
  const data = Route.useLoaderData();
  const { jobCategoriesWithTags, locations } = rootRoute.useRouteContext();

  useEffect(() => {
    trackPageView("search");
  }, []);

  return (
    <>
      <BreadcrumbJsonLd
        items={[{ name: "Home", url: SITE_URL }, { name: "Remote Data Jobs" }]}
      />
      {data.initialJobs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: jsonLdSafe({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "Remote Data Jobs",
              numberOfItems: data.initialTotal,
              itemListElement: data.initialJobs.map((job, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${SITE_URL}/${job.slug}`,
                name: job.role,
              })),
            }),
          }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdSafe(
            data.tagStats
              ? buildFaqSchema("Data", data.tagStats, { isRemotePage: true })
              : buildMinimalFaqSchema("Data", data.initialTotal, {
                  isRemotePage: true,
                }),
          ),
        }}
      />
      <Hero
        title={
          <>
            Remote <span className="text-polaris-orange">data jobs</span>
          </>
        }
        subtitle="Every remote data role tagged by stack, seniority, and timezone, so the right move is easier to spot."
        stats={{ jobs: data.initialTotal }}
        hideBrowseLink
      />
      <SearchClient
        key="remote-data-jobs"
        jobCategoriesWithTags={jobCategoriesWithTags}
        locations={locations}
        initialJobs={data.initialJobs}
        initialTotal={data.initialTotal}
        initialTagSlugs={["remote-data-jobs"]}
      />
    </>
  );
}
